// src/services/AuthService.js
import jwtDecode from 'jwt-decode'
import axios from 'axios'

const AUTH_KEY = 'satnam.auth'
const USER_KEY = 'satnam.user'
const SKEW_SECONDS = 30   // small clock skew

/* ---------- storage helpers ---------- */
const readAuth = () => {
  try {
    const raw = localStorage.getItem(AUTH_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const { access, refresh } = parsed
    if (!access) return null
    return { access, refresh }
  } catch {
    return null
  }
}

const writeAuth = (auth) => {
  localStorage.setItem(AUTH_KEY, JSON.stringify(auth))
}

export const clearAuth = () => {
  localStorage.removeItem(AUTH_KEY)
  localStorage.removeItem(USER_KEY)
  delete axios.defaults.headers.common.Authorization
}

/* ---------- token decode / validity ---------- */
export const decodeJwt = (token) => {
  try { return jwtDecode(token) } catch { return null }
}

export const isTokenValid = (token, skewSeconds = SKEW_SECONDS) => {
  const payload = decodeJwt(token)
  if (!payload || typeof payload.exp !== 'number') return false
  const now = Math.floor(Date.now() / 1000)
  return payload.exp - skewSeconds > now
}

export const isTokenExpired = () => !isTokenValid(getAccessToken(), 0)

/* ---------- access/refresh + claims ---------- */
export const getAccessToken   = () => readAuth()?.access
export const getRefreshToken  = () => readAuth()?.refresh
export const getAuthClaims    = () => decodeJwt(getAccessToken())

// Useful when you only need the id to fetch the user from API
export const getUserIdFromClaims = () => {
  const c = getAuthClaims()
  return c?.id ?? c?.user_id ?? null
}

/* ---------- server user (canonical) ---------- */
// Always prefer the server-hydrated user saved in USER_KEY.
// Do NOT reconstruct and persist the token payload here.
export const getUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const setUser = (userObj) => {
  localStorage.setItem(USER_KEY, JSON.stringify(userObj))
}

export const isStaff = () => {
  const u = getUser()
  return !!(u && (u.is_staff === true || u.is_staff === 'true' || u.is_staff === 1))
}

/* ---------- axios wiring & bootstrap ---------- */
export const setAuth = (auth) => {
  if (!auth || !auth.access) { clearAuth(); return }
  writeAuth(auth)
  axios.defaults.headers.common.Authorization = `Bearer ${auth.access}`
  // IMPORTANT: do NOT overwrite USER_KEY here with JWT payload
}

// Validate token from storage on app boot.
// Returns { ok: boolean, claims: object|null }
export const bootstrapAuthFromStorage = () => {
  const auth = readAuth()
  if (!auth || !isTokenValid(auth.access)) {
    clearAuth()
    return { ok: false, claims: null }
  }
  axios.defaults.headers.common.Authorization = `Bearer ${auth.access}`
  // IMPORTANT: do NOT overwrite USER_KEY here. Keep whatever server user we had.
  const claims = getAuthClaims()
  return { ok: true, claims }
}

/* ---------- expiry scheduler ---------- */
let expTimerId
export const scheduleTokenExpiry = (onExpired, skewSeconds = SKEW_SECONDS) => {
  clearTimeout(expTimerId)
  const token = getAccessToken()
  const payload = decodeJwt(token)
  if (!payload || typeof payload.exp !== 'number') {
    onExpired?.()
    return null
  }
  const fireAtMs = (payload.exp - skewSeconds) * 1000
  const delay = Math.max(0, fireAtMs - Date.now())
  expTimerId = setTimeout(() => onExpired?.(), delay)
  return expTimerId
}

/* ---------- server hydration helpers ---------- */
// Fetch the canonical user from your API and persist it.
// Uses id from claims, so it works even when USER_KEY is empty after a hard refresh.
export const refreshUserFromAPI = async (baseUrl) => {
  const id = getUserIdFromClaims()
  if (!id) return null
  const { data } = await axios.get(
    `${baseUrl}/api/users/${id}/`,
    {
      timeout: 5000,
      headers: { Authorization: `Bearer ${getAccessToken()}` },
      // cache buster to defeat any stale proxies
      params: { _ts: Date.now() },
    }
  )
  setUser(data)
  return data
}

/* ---------- optional: server verification ---------- */
export const verifyWithServer = async (baseUrl) => {
  try {
    await axios.get(`${baseUrl}/api/me/`, { timeout: 3000 })
    return { ok: true, unauthorized: false };
  } catch (e){
    const status = e?.response?.status;
    const unauthorized = status === 401 || status === 403;
    return { ok: false, unauthorized };
  }
}
